import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import Pagination from "components/project/Pagination";
import { getNoticeList, getNoticeDetail } from "lib/api/publicNotice";
import {
  safeStr,
  formatLocaleDateTime,
  isPinnedActive,
} from "lib/utils/notice/noticeUtils";
import HelpTip from "components/HelpTip";

const PAGE_SIZE = 12;

function resolveLocaleTag(lang) {
  const value = String(lang || "ko-KR");
  if (value.startsWith("en")) return "en-US";
  if (value.startsWith("ja")) return "ja-JP";
  if (value.startsWith("vi")) return "vi-VN";
  if (value.startsWith("zh")) return "zh-CN";
  return "ko-KR";
}

export default function NoticePage() {
  const { t, i18n } = useTranslation();

  const localeTag = useMemo(
    () => resolveLocaleTag(i18n.resolvedLanguage || i18n.language),
    [i18n.language, i18n.resolvedLanguage],
  );

  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);

  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [errorList, setErrorList] = useState("");
  const [errorDetail, setErrorDetail] = useState("");

  const detailCacheRef = useRef(new Map());

  const totalPages = useMemo(() => {
    const count = Math.ceil((Number(total) || 0) / PAGE_SIZE);
    return Math.max(1, count);
  }, [total]);

  useEffect(() => {
    setPage(1);
    detailCacheRef.current.clear();
    setDetail(null);
    setSelectedId(null);
  }, [q]);

  useEffect(() => {
    detailCacheRef.current.clear();
    setDetail(null);
  }, [localeTag]);

  useEffect(() => {
    const ac = new AbortController();

    (async () => {
      setLoadingList(true);
      setErrorList("");

      const response = await getNoticeList({
        q: q.trim(),
        page,
        limit: PAGE_SIZE,
        locale: localeTag,
        signal: ac.signal,
      });

      if (ac.signal.aborted) return;

      if (!response?.success) {
        if (response?.message === "ABORTED") return;

        setErrorList(
          t("noticePage.errors.loadList", {
            defaultValue: "공지 목록을 불러오지 못했습니다. 다시 시도해 주세요.",
          }),
        );
        setItems([]);
        setTotal(0);
        setSelectedId(null);
        setDetail(null);
        return;
      }

      const list = Array.isArray(response.items) ? response.items : [];
      setTotal(Number(response.totalCount ?? 0));
      setItems(list);

      if (list.length) {
        setSelectedId((prev) => {
          const exists = list.some((item) => String(item?.id) === String(prev));
          return exists ? prev : String(list[0]?.id);
        });
      } else {
        setSelectedId(null);
        setDetail(null);
      }
    })()
      .catch((error) => {
        if (ac.signal.aborted) return;
        setErrorList(
          error?.message ||
            t("noticePage.errors.loadList", {
              defaultValue: "공지 목록을 불러오지 못했습니다. 다시 시도해 주세요.",
            }),
        );
        setItems([]);
        setTotal(0);
        setSelectedId(null);
        setDetail(null);
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoadingList(false);
      });

    return () => ac.abort();
  }, [q, page, localeTag, t]);

  useEffect(() => {
    if (!selectedId) return;

    const cacheKey = `${localeTag}:${selectedId}`;
    const cached = detailCacheRef.current.get(cacheKey);
    if (cached) {
      setDetail(cached);
      setErrorDetail("");
      return;
    }

    const ac = new AbortController();

    (async () => {
      setLoadingDetail(true);
      setErrorDetail("");

      const response = await getNoticeDetail({
        id: selectedId,
        locale: localeTag,
        signal: ac.signal,
      });

      if (ac.signal.aborted) return;

      if (!response?.success) {
        if (response?.message === "ABORTED") return;
        setErrorDetail(
          t("noticePage.errors.loadDetail", {
            defaultValue: "공지 내용을 불러오지 못했습니다. 다시 시도해 주세요.",
          }),
        );
        setDetail(null);
        return;
      }

      const item = response?.item ?? null;
      setDetail(item);
      if (item) detailCacheRef.current.set(cacheKey, item);
    })()
      .catch((error) => {
        if (ac.signal.aborted) return;
        setErrorDetail(
          error?.message ||
            t("noticePage.errors.loadDetail", {
              defaultValue: "공지 내용을 불러오지 못했습니다. 다시 시도해 주세요.",
            }),
        );
        setDetail(null);
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoadingDetail(false);
      });

    return () => ac.abort();
  }, [selectedId, localeTag, t]);

  const selectedFromList = useMemo(
    () => items.find((item) => String(item?.id) === String(selectedId)) ?? null,
    [items, selectedId],
  );

  const mergedDetail = detail || selectedFromList;

  const handlePrev = () => setPage((prev) => Math.max(1, prev - 1));
  const handleNext = () => setPage((prev) => Math.min(totalPages, prev + 1));
  const handlePage = (nextPage) => setPage(Number(nextPage) || 1);

  return (
    <div className="content noticePage">
      <div className="helpTitleRow">
        <h2 className="pageTitle">{t("noticePage.title")}</h2>
        <HelpTip />
      </div>
      <p className="muted">{t("noticePage.subtitle")}</p>

      <div className="toolRow">
        <div className="searchBox">
          <span className="icon muted" aria-hidden="true">
            <Search size={16} strokeWidth={2.2} />
            ⌕
          </span>
          <input
            className="input"
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder={t("noticePage.searchPlaceholder")}
            autoComplete="off"
          />
        </div>

        <div className="countText muted">
          {t("noticePage.totalCount", {
            count: Number(total).toLocaleString(localeTag),
          })}
        </div>
      </div>

      <div className="noticeGrid">
        <div className="card noticeListCard">
          <div className="cardHeader">
            <h3>{t("noticePage.listTitle")}</h3>
            <span className="hint muted">
              {loadingList
                ? t("noticePage.loading")
                : t("noticePage.pageStatus", { page, totalPages })}
            </span>
          </div>

          {errorList ? (
            <div className="emptyPanel">
              <p style={{ margin: 0, fontWeight: 900 }}>
                {t("noticePage.loadListFailed")}
              </p>
              <p className="muted" style={{ margin: "6px 0 0" }}>
                {errorList}
              </p>
            </div>
          ) : items.length === 0 ? (
            <div className="emptyPanel">
              {loadingList ? t("noticePage.loading") : t("noticePage.emptyList")}
            </div>
          ) : (
            <div className="tableWrap">
              <div className="noticeTableHead">
                <span className="th pin">{t("noticePage.table.pinned")}</span>
                <span className="th title">{t("noticePage.table.title")}</span>
                <span className="th date">
                  {t("noticePage.table.publishedAt")}
                </span>
                <span className="th views">{t("noticePage.table.views")}</span>
              </div>

              <ul className="noticeTableBody">
                {items.map((item) => {
                  const id = String(item?.id ?? "");
                  const isSelected = id === String(selectedId);

                  const pinnedOn = isPinnedActive(item, new Date());
                  const title = safeStr(item?.title) || t("noticePage.untitled");
                  const summary = safeStr(item?.summary);
                  const dateText = formatLocaleDateTime(
                    item?.published_at ?? item?.created_at,
                    localeTag,
                  );
                  const views = Number(item?.view_count ?? 0);

                  return (
                    <li
                      key={id}
                      className={`noticeRow ${isSelected ? "isSelected" : ""}`}
                      onClick={() => setSelectedId(id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setSelectedId(id);
                        }
                      }}
                      title={title}
                    >
                      <span className="td pin">
                        {pinnedOn ? (
                          <span className="badge badge-pin">
                            {t("noticePage.pinned")}
                          </span>
                        ) : (
                          <span className="muted">-</span>
                        )}
                      </span>

                      <span className="td title">
                        <b className="rowTitle">{title}</b>
                        {summary ? (
                          <span className="rowSummary muted">{summary}</span>
                        ) : null}
                      </span>

                      <span className="td date muted">{dateText}</span>

                      <span className="td views muted">
                        {views.toLocaleString(localeTag)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <Pagination
            page={page}
            limit={PAGE_SIZE}
            totalCount={total}
            onPrev={handlePrev}
            onNext={handleNext}
            onPage={handlePage}
          />
        </div>

        <div className="card noticeDetailCard">
          <div className="cardHeader">
            <h3>{t("noticePage.detailTitle")}</h3>
            <span className="hint muted">{t("noticePage.latestOnly")}</span>
          </div>

          {errorDetail ? (
            <div className="emptyPanel">
              <p style={{ margin: 0, fontWeight: 900 }}>
                {t("noticePage.loadDetailFailed")}
              </p>
              <p className="muted" style={{ margin: "6px 0 0" }}>
                {errorDetail}
              </p>
            </div>
          ) : !selectedId ? (
            <div className="emptyPanel">{t("noticePage.selectNotice")}</div>
          ) : loadingDetail && !mergedDetail ? (
            <div className="emptyPanel">{t("noticePage.loadingDetail")}</div>
          ) : !mergedDetail ? (
            <div className="emptyPanel">{t("noticePage.emptyDetail")}</div>
          ) : (
            <div className="detailBody">
              <div className="detailTitle">
                <div className="titleRow">
                  <h4 style={{ margin: 0 }}>
                    {safeStr(mergedDetail?.title) || t("noticePage.untitled")}
                  </h4>

                  {isPinnedActive(mergedDetail, new Date()) ? (
                    <span className="badge badge-pin" style={{ marginLeft: 10 }}>
                      {t("noticePage.pinned")}
                    </span>
                  ) : null}
                </div>

                <div className="detailMeta muted">
                  <span>
                    {t("noticePage.publishedAt")}{" "}
                    {formatLocaleDateTime(
                      mergedDetail?.published_at ?? mergedDetail?.created_at,
                      localeTag,
                    )}
                  </span>

                  <span style={{ marginLeft: 12 }}>
                    {t("noticePage.viewsLabel")}{" "}
                    {Number(mergedDetail?.view_count ?? 0).toLocaleString(
                      localeTag,
                    )}
                  </span>
                </div>

                {safeStr(mergedDetail?.summary) ? (
                  <p className="detailSummary muted">{safeStr(mergedDetail?.summary)}</p>
                ) : null}
              </div>

              <div className="divider" />

              {safeStr(mergedDetail?.body) ? (
                <pre className="noticeBodyText">{safeStr(mergedDetail?.body)}</pre>
              ) : loadingDetail ? (
                <div className="emptyPanel" style={{ padding: 18 }}>
                  {t("noticePage.loadingBody")}
                </div>
              ) : (
                <div className="emptyPanel" style={{ padding: 18 }}>
                  {t("noticePage.emptyContent")}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
